import { Award, BicepsFlexed, Drama, Frown, ThumbsDown } from 'lucide-react';
import React from 'react';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

type Props = {
  discountStatus: 'accept' | 'bad' | 'best' | 'only' | 'static';
};

function DiscountMap({ discountStatus }: Props) {
  // Map each RequestType to its corresponding label, background, and text colors.
  const typeMapping: Record<
    string,
    {
      bg: string;
      text: string;
      icon: React.ReactNode;
      border: string;
      tooltip: string;
    }
  > = {
    accept: {
      bg: 'bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-400/20 dark:to-gray-500/20',
      text: 'text-gray-700 dark:text-gray-50',
      icon: <Frown className="h-6 w-6" />,
      border: 'border-gray-300/30 dark:border-gray-400/30',
      tooltip: 'مقبول - خصم في المعدل الطبيعي',
    },
    bad: {
      bg: 'bg-gradient-to-br from-red-100 to-red-200 dark:from-red-400/20 dark:to-red-500/20',
      text: 'text-red-500 dark:text-red-50',
      icon: <ThumbsDown className="h-6 w-6" />,
      border: 'border-red-300/30 dark:border-red-400/30',
      tooltip: 'سيء - خصم غير مناسب',
    },
    best: {
      bg: 'bg-gradient-to-br from-green-100 to-green-200 dark:from-green-400/20 dark:to-green-500/20',
      text: 'text-green-700 dark:text-green-50',
      icon: <Award className="h-6 w-6" />,
      border: 'border-green-300/30 dark:border-green-400/30',
      tooltip: 'ممتاز - خصم مميز جداً',
    },
    only: {
      bg: 'bg-gradient-to-br from-indigo-100 to-indigo-200 dark:from-indigo-400/20 dark:to-indigo-500/20',
      text: 'text-indigo-700 dark:text-indigo-50',
      icon: <BicepsFlexed className="h-6 w-6" />,
      border: 'border-indigo-300/30 dark:border-indigo-400/30',
      tooltip: 'حصري - خصم خاص بنا فقط',
    },
    static: {
      bg: 'bg-gradient-to-br from-fuchsia-100 to-fuchsia-200 dark:from-fuchsia-400/20 dark:to-fuchsia-500/20',
      text: 'text-fuchsia-700 dark:text-fuchsia-50',
      icon: <Drama className="h-6 w-6" />,
      border: 'border-fuchsia-300/30 dark:border-fuchsia-400/30',
      tooltip: 'ثابت - سعر ثابت بدون خصم',
    },
  };

  // Fallback for any unmatched type
  const typeData = typeMapping[discountStatus];

  return (
    <TooltipProvider>
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <div
            className={`flex h-7 w-12 items-center justify-center overflow-hidden rounded-md border ${typeData.border} ${typeData.bg} ${typeData.text} cursor-help font-medium`}
          >
            {typeData.icon}
          </div>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          align="center"
          className="max-w-[200px] text-center"
        >
          <p className="font-medium">{typeData.tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default DiscountMap;
